import { declarationGroups } from '../data/declarations';
import type { Answers } from '../types/application';
import { formatCurrency, formatDate, parseAmount, yearsSince } from './format';
import { isStepVisible } from './applicationFlow';

export interface ReviewRow {
  label: string;
  value: string;
}

export interface ReviewSection {
  id: string;
  title: string;
  editStep: string;
  rows: ReviewRow[];
  issues: { message: string; step: string }[];
}

const labels: Record<string, string> = {
  purchase: 'Buying a home',
  refinance: 'Refinancing',
  contract: 'Under contract',
  offers: 'Making offers',
  looking: 'Still looking',
  single: 'Single-family home',
  condo: 'Condo',
  townhouse: 'Townhouse',
  multi: '2–4 unit property',
  primary: 'Primary residence',
  second: 'Second home',
  investment: 'Investment property',
  married: 'Married',
  separated: 'Separated',
  unmarried: 'Unmarried',
  citizen: 'U.S. citizen',
  permanent: 'Permanent resident',
  nonpermanent: 'Non-permanent resident',
  own: 'Own',
  rent: 'Rent',
  free: 'Living rent-free',
  w2: 'Employed (W-2)',
  self: 'Self-employed',
  retired: 'Retired',
  none: 'Not currently employed',
};

export const label = (v: string) => labels[v] ?? (v || '—');

export function buildReview(a: Answers): ReviewSection[] {
  const loanAmount = Math.max(0, a.price - a.downPayment);
  const missing = (cond: boolean, message: string, step: string) => (cond ? [{ message, step }] : []);

  const answeredDecl = declarationGroups
    .filter((g) => isStepVisible(g.stepId, a))
    .flatMap((g) => g.questions.map((q) => ({ q, step: g.stepId })));
  const unanswered = answeredDecl.filter(({ q }) => !a.declarations[q.id]);
  const yesCount = answeredDecl.filter(({ q }) => a.declarations[q.id] === 'yes').length;

  const totalAssets = a.assets.reduce((s, x) => s + parseAmount(x.balance), 0);
  const monthlyIncome = a.employers.reduce((s, e) => {
    const base = parseAmount(e.basePay);
    const m = e.payFrequency === 'annual' ? base / 12 : e.payFrequency === 'hourly' ? (base * 2080) / 12 : base;
    return s + m + parseAmount(e.bonus) / 12 + parseAmount(e.commission) / 12 + parseAmount(e.overtime) / 12;
  }, 0);
  const otherMonthly = a.otherIncome.reduce((s, i) => s + parseAmount(i.monthly), 0);
  const employmentYears = a.employers.reduce((s, e) => s + yearsSince(e.startDate), 0);

  return [
    {
      id: 'personal',
      title: 'Personal information',
      editStep: 'name',
      rows: [
        { label: 'Legal name', value: [a.firstName, a.middleName, a.lastName, a.suffix].filter(Boolean).join(' ') },
        { label: 'Email', value: a.email },
        { label: 'Phone', value: a.phone },
        { label: 'Date of birth', value: a.dob ? formatDate(a.dob) : '' },
        { label: 'Social Security number', value: a.ssn.length === 9 ? `•••-••-${a.ssn.slice(-4)}` : '' },
        { label: 'Marital status', value: label(a.maritalStatus) },
        { label: 'Citizenship', value: label(a.citizenship) },
        { label: 'Current address', value: `${a.currentStreet}, ${a.currentCity}, ${a.currentState} ${a.currentZip}` },
        { label: 'Housing', value: a.housing === 'rent' ? `Rent · ${formatCurrency(parseAmount(a.rent))}/mo` : label(a.housing) },
      ],
      issues: [
        ...missing(!a.dob, 'Date of birth is missing', 'contact'),
        ...missing(a.ssn.length !== 9, 'Social Security number is missing', 'contact'),
        ...missing(!a.firstName || !a.lastName, 'Legal name is incomplete', 'name'),
        ...missing(
          parseAmount(a.yearsAtAddress) < 2 && !a.prevStreet,
          'Add your previous address — you’ve lived at your current one less than 2 years',
          'home',
        ),
      ],
    },
    {
      id: 'property',
      title: 'Property',
      editStep: 'type',
      rows: [
        ...(a.goal === 'purchase' ? [{ label: 'Status', value: label(a.propertyStage) }] : []),
        { label: 'Address', value: a.propertyStreet ? `${a.propertyStreet}, ${a.propertyCity}, ${a.propertyState} ${a.propertyZip}` : 'To be determined' },
        { label: 'Type', value: label(a.propertyType) },
        { label: 'Use', value: label(a.occupancy) },
      ],
      issues: missing(!a.propertyType, 'Choose a property type', 'type'),
    },
    {
      id: 'employment',
      title: 'Employment',
      editStep: 'employer',
      rows: a.employers.length
        ? a.employers.map((e) => ({
            label: e.name || 'Employer',
            value: `${e.position || 'Position missing'} · since ${e.startDate ? formatDate(e.startDate) : '—'}`,
          }))
        : [{ label: 'Employment', value: label(a.employmentType) }],
      issues: [
        ...a.employers.flatMap((e) => missing(!e.startDate || !e.name, `${e.name || 'An employer'} is missing details`, 'employer')),
        ...missing(
          a.employmentType === 'w2' && a.employers.length > 0 && employmentYears < 2,
          'We need 2 years of employment history — add your previous employer',
          'employer',
        ),
      ],
    },
    {
      id: 'income',
      title: 'Income',
      editStep: 'pay',
      rows: [
        { label: 'From employment', value: `${formatCurrency(monthlyIncome)}/mo` },
        { label: 'Other income', value: otherMonthly ? `${formatCurrency(otherMonthly)}/mo` : 'None' },
      ],
      issues: missing(a.employmentType === 'w2' && monthlyIncome === 0, 'Add your pay', 'pay'),
    },
    {
      id: 'assets',
      title: 'Assets',
      editStep: 'accounts',
      rows: a.assets.map((x) => ({ label: `${x.institution || 'Account'} · ${x.type}`, value: formatCurrency(parseAmount(x.balance)) })).concat([
        { label: 'Total', value: formatCurrency(totalAssets) },
      ]),
      issues: missing(
        a.goal === 'purchase' && totalAssets < a.downPayment,
        `Your accounts total less than your ${formatCurrency(a.downPayment)} down payment`,
        'accounts',
      ),
    },
    {
      id: 'real-estate',
      title: 'Real estate',
      editStep: 'real-estate',
      rows:
        a.ownsOtherProperty === 'yes'
          ? a.properties.map((p) => ({ label: p.address || 'Property', value: formatCurrency(parseAmount(p.value)) }))
          : [{ label: 'Other properties', value: a.ownsOtherProperty === 'no' ? 'None' : '' }],
      issues: missing(!a.ownsOtherProperty, 'Let us know if you own other property', 'real-estate'),
    },
    {
      id: 'loan',
      title: 'Loan information',
      editStep: 'price',
      rows: [
        { label: 'Purpose', value: label(a.goal) },
        { label: a.goal === 'refinance' ? 'Estimated value' : 'Purchase price', value: formatCurrency(a.price) },
        {
          label: a.goal === 'refinance' ? 'Equity' : 'Down payment',
          value: `${formatCurrency(a.downPayment)} (${a.price ? Math.round((a.downPayment / a.price) * 100) : 0}%)`,
        },
        { label: 'Loan amount', value: formatCurrency(loanAmount) },
      ],
      issues: [],
    },
    {
      id: 'declarations',
      title: 'Declarations',
      editStep: 'decl-property',
      rows: [
        {
          label: 'Answered',
          value: `${answeredDecl.length - unanswered.length} of ${answeredDecl.length}${yesCount ? ` · ${yesCount} answered “Yes”` : ''}`,
        },
      ],
      issues: unanswered.length
        ? [{ message: `${unanswered.length} question${unanswered.length > 1 ? 's' : ''} still need an answer`, step: unanswered[0].step }]
        : [],
    },
  ];
}
